import Loader from '@/src/components/Loading/Loader'
import Modal from '@/src/components/Modal/Modal'
import FrameSelector from '@/src/components/Player/GameFrame/FrameSelector'
import styles from '@/src/components/Player/GameFrame/GameFrame.module.scss'
import RealityCheck from '@/src/components/RealityCheck/RealityCheck'
import SGABar from '@/src/components/SGABar'
import StatusScreen from '@/src/components/StatusScreen'
import { Heading } from '@/src/components/Typography'
import useRouter from '@/src/hooks/Router/useRouter'
import useAuthGuard from '@/src/hooks/useAuthGuard'
import useTabDetection from '@/src/hooks/useTabDetection'
import useTimeout from '@/src/hooks/useTimeout'
import { usePlayGameMutation } from '@/src/services/games'
import { gtmGameStart } from '@/src/services/gtm_factory.ts'
import {
  useGetBalanceQuery,
  useGetRealityCheckQuery,
} from '@/src/services/player'
import { FamilyType } from '@/src/services/types/games'
import { closeModal, showModal } from '@/src/store/slices/modalSlice'
import { gameByDevice } from '@/src/utils/helpers/thumbnails'
import localStorageService from '@/src/utils/localStorage'
import { addBreadcrumb, captureMessage } from '@sentry/nextjs'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

type GameFrameProps = {
  gameFamily: FamilyType
}

const GameFrame = ({ gameFamily }: GameFrameProps) => {
  const { t } = useTranslation(['game', 'compliance', 'common'])
  const { isLoggedIn } = useAuthGuard()
  const { isLatestTab } = useTabDetection()
  const [iframeLoading, setIframeLoading] = useState(true)
  // TODO: Handle game error
  const [playGame, { data: gameData }] = usePlayGameMutation()
  const { data: balance } = useGetBalanceQuery(undefined, {
    pollingInterval: 5000,
    skip: !isLoggedIn,
  })
  const { data: realityCheck, refetch } = useGetRealityCheckQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: gameData?.providerRCEnabled,
  })

  const dispatch = useDispatch()
  const router = useRouter()

  useEffect(() => {
    if (
      balance?.realityCheckReached &&
      gameData?.providerRCEnabled === false &&
      realityCheck?.realityCheck
    ) {
      dispatch(showModal('reality-check'))
    }
  }, [
    balance?.realityCheckReached,
    dispatch,
    gameData?.providerRCEnabled,
    realityCheck?.realityCheck,
  ])

  useEffect(() => {
    window.document.documentElement.classList.toggle(`app-game`, true)
    return () => {
      window.document.documentElement.classList.toggle(`app-game`, false)
    }
  }, [])

  useEffect(() => {
    const game = gameByDevice(gameFamily.games)
    playGame({
      mode: 'real',
      name: game.name,
      feSessionId: localStorageService.getFeSessionIdToken() || undefined,
      feProfileSessionId:
        localStorageService.getFeProfileSessionIdToken() || undefined,
    })
  }, [gameFamily, playGame])

  useEffect(() => {
    if (!gameData) return

    if (gameData?.error || gameData?.errorCode) {
      addBreadcrumb({
        category: 'gamePlay',
        message: 'Game launch url failed',
        data: gameData,
        level: 'fatal',
      })
      captureMessage(`PlayUrl failed with: ${gameData.errorCode}`, 'fatal')
      router.replace(`/error/${gameData?.errorCode || 100}`)
      return
    }

    gtmGameStart({ gameName: gameData?.name })
  }, [gameData, gameFamily, router, dispatch])

  useTimeout(() => {
    setIframeLoading(false)
  }, 10000)

  useEffect(() => {
    return () => {
      dispatch(closeModal('reality-check'))
    }
  }, [dispatch])

  return (
    <>
      {iframeLoading && (
        <div className={cn(styles.loader)}>
          <div className='flex h-full w-full flex-col items-center justify-center text-center font-bold'>
            <Loader
              header={t('now-launching', {
                ns: 'game',
                name: gameFamily.familyName,
              })}
              type='inline'
            />
          </div>
        </div>
      )}
      <SGABar
        isLoggedIn={isLoggedIn}
        isInGame
        gameName={!iframeLoading ? gameFamily?.familyName : undefined}
        searchReplaceUrl
      />
      <Modal
        targetModal='reality-check'
        allowClickOutside={false}
        allowClose={false}
      >
        <RealityCheck
          balance={balance}
          closeEvent={() => dispatch(closeModal('reality-check'))}
          providerRCEnabled={gameData?.providerRCEnabled || false}
          realityCheck={realityCheck}
          refetch={refetch}
        />
      </Modal>

      {isLatestTab ? (
        !!gameData && (
          <FrameSelector
            gameFamily={gameFamily}
            gameUrl={gameData.url}
            setIframeLoading={setIframeLoading}
            classNames={cn(
              { 'pointer-events-none h-screen': !isLoggedIn },
              { [styles.iframe]: isLoggedIn },
            )}
          />
        )
      ) : (
        <StatusScreen classes='relative'>
          <Heading level={1}>{t('common:error')}</Heading>
          <p className='mb-8 px-8 text-center opacity-80'>
            {t('game:only-one-game-tab')}
          </p>
        </StatusScreen>
      )}
    </>
  )
}

export default GameFrame
